/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useContext, useEffect, useRef, useState} from "react"
import styles from "./Topics.module.css"
import {ControlledSearch, Message} from "sections/NewCommunication/shared"
import {Communication} from "types/communication"
import {chatServiceV2} from "services"
import {useInfiniteQuery, useQuery} from "@tanstack/react-query"
import {handleErrorChat} from "helpers"
import {Spin} from "antd"
import {useDebouncedValue} from "hooks"
import cx from "classnames"
import {CommunicationContext} from "context/CommunicationContext"

type Props = {
    studentRoomId: number
    activeTopic?: Communication.ChannelTopic
    onSelectTopic?: (topic: Communication.ChannelTopic) => void
}

export function Topics(props: Props) {
    const {studentRoomId, onSelectTopic, activeTopic} = props
    const [searchText, setSearchText] = useState<string>("")
    const debouncedSearchText = useDebouncedValue(searchText)
    const observer = useRef<IntersectionObserver>()
    const [topics, setTopics] = useState<Communication.ChannelTopic[]>([])
    const {changeRoom} = useContext(CommunicationContext)

    useEffect(() => {
        setSearchText("")
        setTopics([])
    }, [studentRoomId])

    const fetchData = async ({pageParam = 1}) => {
        const {data, total} = await chatServiceV2.getChannelTopics({
            filter: {
                parentRoomId: studentRoomId,
                search: debouncedSearchText
            },
            range: {pageSize: 10, page: pageParam}
        })
        setTopics((prev) => (pageParam === 1 ? data : [...prev, ...data]))
        return {results: data, nextPage: pageParam + 1, totalPages: Math.ceil(total / 10)}
    }

    const {hasNextPage, fetchNextPage, isFetching, isLoading, refetch} = useInfiniteQuery(
        ["student-channel-topics", studentRoomId, debouncedSearchText],
        fetchData,
        {
            getNextPageParam: (lastPage, pages) => {
                if (lastPage.nextPage <= lastPage.totalPages) return lastPage.nextPage
                return undefined
            },
            onError: (err) => {
                handleErrorChat(err)
            },
            enabled: !!studentRoomId
        }
    )

    const lastElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (isLoading) return

            if (observer.current) observer.current.disconnect()

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetching) {
                    fetchNextPage()
                }
            })

            if (node) observer.current.observe(node)
        },
        [fetchNextPage, hasNextPage, isFetching, isLoading]
    )

    const renderMessages = () => {
        return (
            <div className={styles.main}>
                {(topics || []).map((item) => {
                    return (
                        <div key={item.roomId} ref={lastElementRef}>
                            <div
                                className={cx(styles.messageContainer, {
                                    [styles.active]: activeTopic?.roomId === item.roomId
                                })}
                                onClick={() => {
                                    onSelectTopic?.(item)
                                    changeRoom(item)
                                }}>
                                <span className={styles.topicTitle}>{item.name}</span>
                            </div>
                        </div>
                    )
                })}
                {isFetching && <Spin className={styles.loadingData} />}
            </div>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.heading}>
                <ControlledSearch
                    value={searchText}
                    onChangeValue={setSearchText}
                    className={styles.search}
                    placeholder="Type what you want to search"
                />
            </div>
            {renderMessages()}
        </div>
    )
}
